import { AnimalAgent, BuildInfo, BundleInfo, DatabaseRecord, ImageReference, Mind, UserAgent, UserRiskProfile } from ".";
import { DocumentReference, Timestamp } from "@um/contracts/firestore";
import type { PurchaseInfo } from "./revenueCat";
import type { AccountRetentionState } from "./retention";
import { DeviceSettings } from "./settings";
import { TestResourceGroup } from "./TestResources";

export type AccountType =
  | "vendor"
  | "user"
  | "bot"
  | "reviewer"
  | "internal"
  | "anonymous"
  | "clinician"
  | "champion"
  | "wholesaler"
  | "disabled";

/** Automatic support-crew election and onboarding belong to personal accounts. */
export function supportsAutomaticCaptain(account: { type?: unknown } | null | undefined): boolean {
  return account?.type === "user" || account?.type === "anonymous";
}

export type WaiverStatus =
  | "new"
  | "contacted"
  | "awaiting_case_manager"
  | "case_manager_contacted"
  | "reimbursement_submitted"
  | "reimbursed"
  | "closed";

export interface Account extends Partial<PurchaseInfo>, DatabaseRecord {
  name: string;
  users: DocumentReference<User>[]; // denormalized for lookup for security rules
  email: string;
  contactEmail?: string;
  inviteCodes?: string[]; // denormalized for lookup by invite system
  isInternal?: boolean;
  type: AccountType;
  disabledReason?: string;
  source?: string;
  image?: ImageReference;
  userRoles?: UserRole[];
  sus?: boolean; // accounts may be bogus
  stats?: AccountStats;
  clinician?: ClinicianInfo;
  champion?: ChampionInfo;
  wholesaler?: WholesalerInfo;
  conversionData?: ConversionData;
  waiverStatus?: WaiverStatus;
  /** Internal Unique Minds staff user assigned to manage this account (e.g. waiver reimbursement). */
  accountManager?: DocumentReference<User>;
  disabledUsers?: DocumentReference<User>[];
  testResourceGroup?: DocumentReference<TestResourceGroup>;
  attributes?: Record<string, string>; // for audience targeting with revenue cat
  /** Win-back bookkeeping written by the retention backend (docs/retention.md). */
  retention?: AccountRetentionState;
}

export interface ClinicianInfo {
  clinicianType: string;
  practiceType: PracticeType;
  message: string;
  messageFrom: string;
  messageFromImage?: ImageReference;
  codes: string[];
  guide?: AnimalAgent;
}

export type PracticeType = "independent" | "group";

export interface ChampionInfo {
  whoTheyHelp: string;
  howTheyHelp: string;
  messageFrom?: string;
}

/**
 * Minimum business information collected for a wholesaler account. These fields
 * are the bare minimum needed for Washington State sales tax exemption / reseller
 * permit reporting. Wholesaler accounts are created and managed via the admin portal.
 */
export interface WholesalerInfo {
  /** Legal business name as registered with WA Department of Revenue. */
  legalBusinessName: string;
  /** WA Unified Business Identifier (UBI) / tax registration number. */
  ubi: string;
  /** WA reseller permit number used to substantiate the exemption. */
  resellerPermitNumber: string;
  businessAddress: WholesalerBusinessAddress;
  contactName?: string;
  contactPhone?: string;
  /** Download URL of the uploaded reseller certificate document (PDF/image) in Storage. */
  resellerCertificateUrl?: string;
  /** Reseller certificate expiration date, as an ISO `YYYY-MM-DD` string. */
  resellerCertificateExpiration?: string;
}

export interface WholesalerBusinessAddress {
  street: string;
  city: string;
  /** Two-letter state code (e.g. "WA"). */
  state: string;
  zip: string;
}

/** Engagement points for feedback-form timing. Sum = total engagement score. */
export interface EngagementPoints {
  /** Completed mind (profile) — 5 pts each */
  completedMind: number;
  /** Response to activity — 1 pt each */
  activityResponse: number;
  /** Mind count (profile created) — 2 pts each */
  mindCount: number;
  /** Character count (character created) — 1 pt each */
  characterCount: number;
  /** Cached total points across all categories */
  totalPoints: number;
  updated: Timestamp;
}

export interface AccountStats {
  minds: number;
  animals: number;
  devices: number;
  updated: Timestamp;
  lastUserActivity: Timestamp;
  /** Points used to determine when to show feedback form */
  engagementPoints?: EngagementPoints;
}

export interface UserRole {
  agent?: UserAgent;
  mind?: DocumentReference<Mind>;
  // TODO: add a list of minds that restrict what you can see in the app
  invitedBy?: UserAgent;
  greeter?: AnimalAgent;
  inviteEmail?: string;
  inviteStatus?: "pending" | "sent" | "accepted" | "rejected" | "expired";
  inviteCode?: string;
  inviteSendCount?: number;
  parentConsented?: Timestamp;
  parentConsentInformation?: string;
  parentConsentUser?: DocumentReference<User>;
  notificationsEnabled?: boolean;
  role: AccountRole;
  created?: Timestamp;
  updated?: Timestamp;
}

export type AccountRole = "owner" | "member";

// Survey attribution data collected during onboarding
export interface SurveyAttribution {
  source: string;
  details?: string;
  collectedAt: string; // ISO timestamp
}

export interface WebOnboardingHandoff {
  accountId: string;
  characterId: string;
  /** Optional for checkpoints produced before session metadata was added. */
  sessionId?: string;
  userId?: string;
  /** Immutable character creation time from Firestore, never tab refresh time. */
  createdAtMillis?: number;
  minimumAppVersion?: string;
  /** Version 1 finishes explicitly; character-selection flags are not completion. */
  completionVersion?: 1;
  status: "pending" | "resumed" | "complete";
}

// This record schema is defined by FlutterFlow, outside DatabaseRecord.
export interface User {
  uid: string;
  email?: string;
  contactEmail?: string;
  display_name: string;
  photo_url?: string;
  created_time: Timestamp;
  defaultAccount?: DocumentReference<Account>;
  defaultClinicianAccount?: DocumentReference<Account>;
  defaultWholesalerAccount?: DocumentReference<Account>;
  defaultStudioAccount?: DocumentReference<Account>;
  /** Latest web setup; each device acknowledges it separately. */
  webOnboardingHandoff?: WebOnboardingHandoff;
  defaultVendorAccount?: DocumentReference<Account>;
  image?: ImageReference;
  isInternal?: boolean;
  status?: "active" | "inactive";
  type: AccountType;
  disabledReason?: string;
  notificationsEnabled?: boolean;
  testResourceGroup?: DocumentReference<TestResourceGroup>;
  requestedAppReviewCount?: number;

  // attribution fields
  conversionData?: ConversionData;
  attributionData?: any;
  deepLinkData?: {
    link: string;
  };
  surveyAttribution?: SurveyAttribution;
  metaAttribution?: MetaAttribution;
  attributes?: Record<string, string>; // for audience targeting with revenue cat
  riskProfile?: UserRiskProfile;
}

/**
 * Meta (Facebook) click + browser ids, captured from the web landing URL and
 * pinned to the User so they outlive the browser session.
 *
 * Why store them at all: `external_id` lets Meta match the *person*, but `fbc`
 * is what ties an event to the *specific ad click*. The cookies these come from
 * are written by fbevents.js, are capped at 7 days by Safari ITP, and never
 * exist at all when the pixel is blocked — so a purchase weeks later, or any
 * event sent from the native app, has no way to reach back to the ad.
 *
 * Captured on the web (where `fbclid` is on the URL and ATT does not apply) and
 * carried on the user's own id, these survive the web→app handoff: the
 * anonymous web user is upgraded in place at sign-in, so the uid is unchanged.
 * That makes it possible to attribute an in-app conversion to the originating
 * ad even on iOS, which a direct-to-store install campaign cannot do.
 *
 * Note this is deliberately on User, not Account: RevenueCat's subscriber id is
 * the *account* id and it has no concept of users, so anything resolving these
 * for a purchase must go account → user (`Account.users` / `userRoles`) first.
 */
export interface MetaAttribution {
  /** `_fbc` — the click id, in Meta's `fb.1.<createdMs>.<fbclid>` form. */
  fbc?: string;
  /** `_fbp` — the browser id, in Meta's `fb.1.<createdMs>.<random>` form. */
  fbp?: string;
  /** The raw `fbclid` exactly as it arrived, kept for debugging. */
  fbclid?: string;
  /**
   * When the click id was captured — i.e. when the user landed from the ad,
   * not when this document was written. Those can be far apart: the click is
   * captured at the landing page, but only persisted once a uid exists.
   */
  capturedAt?: Timestamp;
  /** Landing URL the click id came from (path + query, no origin). */
  landingUrl?: string;
}

export type ConversionData = {
  // web
  af_status?: string;
  campaign?: string;
  media_source?: string;
  utm_source?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_id?: string;
  utm_medium?: string;
  utm_term?: string;
} & {
  // appsflyer
  ad_id?: string;
  adgroup?: string;
  adgroup_id?: string;
  adset?: string;
  adset_id?: string;
  af_channel?: string;
  af_status?: string;
  campaign?: string;
  campaign_id?: string;
  click_time?: string;
  install_time?: string;
  is_fb?: boolean;
  is_first_launch?: boolean;
  is_mobile_data_terms_signed?: boolean;
  is_paid?: boolean;
  iscache?: boolean;
  match_type?: string;
  media_source?: string;
  retargeting_conversion_type?: string;
};

export interface Device {
  // TODO: should implement DatabaseRecord
  /**
   * The FCM registration token, absent once it is known to be dead.
   *
   * Optional because `handleFailedTokens` deletes it when FCM answers
   * `registration-token-not-registered` — the app was uninstalled, the token
   * rotated, or app data was cleared. Every reader tests it for truthiness, so
   * a device without one is simply not pushed until the app registers again.
   */
  token?: string;
  platform: string;
  osVersion: string;
  model: string;
  manufacturer: string;
  isVirtual: boolean;
  name: string;
  updatedAt: Timestamp;
  build?: BuildInfo;
  bundle?: BundleInfo;
  /**
   * How many pushes to this device have failed. A count, not a flag — it is
   * written with `FieldValue.increment`, and was typed `boolean` for a while
   * against a value that has always been a number.
   */
  failedNotifications?: number;
  lastFailedNotification?: Timestamp;
  timezone?: string; // IANA timezone identifier (e.g., "America/New_York")
  settings?: DeviceSettings;
  selectedMind?: DocumentReference<Mind>;
  selectedRole?: AccountRole;
}
